/**
 * 海螺视频生成节点 - 支持海螺2.3和海螺02两个通道
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './Hailuo23VideoNode.css';

const apiService = new ApiService();

// 工具函数：判断数组是否相等
const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

// 工具函数：合并图片列表（保持原有顺序）
const mergeImageLists = (prev = [], next = []) => {
  const nextQueue = [...next];
  const reordered = [];

  prev.forEach((url) => {
    const index = nextQueue.indexOf(url);
    if (index !== -1) {
      reordered.push(url);
      nextQueue.splice(index, 1);
    }
  });

  return [...reordered, ...nextQueue];
};

// 通道模式选项
const channelOptions = [
  { value: 'hailuo-2.3', label: '海螺2.3' },
  { value: 'hailuo-02', label: '海螺02' }
];

// 固定分辨率：海螺02 和 海螺2.3 都使用 768p
const DEFAULT_RESOLUTION = '768P';

// 时长选项
const durationOptions = [
  { value: 6, label: '6秒' },
  { value: 10, label: '10秒' }
];

export const Hailuo23VideoNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  const [channel, setChannel] = useState(data.channel || 'hailuo-2.3');
  const [duration, setDuration] = useState(data.duration || 6);
  const [promptOptimizer, setPromptOptimizer] = useState(data.promptOptimizer !== undefined ? data.promptOptimizer : true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImages, setConnectedImages] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    updateNodeData(id, {
      channel,
      resolution: DEFAULT_RESOLUTION,
      duration,
      promptOptimizer,
    });
  }, [id, channel, duration, promptOptimizer, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'prompt'
    );
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  // 获取连接到此节点的图片参考节点和任务节点
  const getConnectedImageNodes = useCallback(() => {
    const connectedEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === 'image'
    );
    
    console.log(`Hailuo23VideoNode ${id} - 连接到图片输入的边:`, connectedEdges.length);
    
    const imageUrls = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`Hailuo23VideoNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Hailuo23VideoNode ${id} - 从图片参考节点获取图片:`, url);
        }
      }
      
      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Hailuo23VideoNode ${id} - 从手绘节点获取图片:`, url);
        } else if (sourceNode.data?.isLocalPreview) {
          console.warn(`Hailuo23VideoNode ${id} - 手绘节点图片为本地预览，跳过`);
        }
      }
      
      // 从图片任务节点获取生成的图片（取第一张）
      if (sourceNode && sourceNode.type === 'imageTask') {
        console.log(`Hailuo23VideoNode ${id} - 任务节点数据:`, {
          status: sourceNode.data?.status,
          generatedImages: sourceNode.data?.generatedImages,
          imagesCount: sourceNode.data?.generatedImages?.length
        });
        
        if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
          const firstImage = sourceNode.data.generatedImages[0];
          const url = typeof firstImage === 'string' ? firstImage : firstImage.url;
          if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`Hailuo23VideoNode ${id} - 从任务节点获取第一张图片:`, url);
          }
        }
      }
      
      // 从视频任务节点获取生成的视频封面
      if (sourceNode && sourceNode.type === 'videoTask') {
        if (sourceNode.data?.coverImageUrl) {
          const url = sourceNode.data.coverImageUrl;
          if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`Hailuo23VideoNode ${id} - 从视频任务节点获取封面:`, url);
          }
        }
      }
    });
    
    console.log(`Hailuo23VideoNode ${id} - 总共获取到 ${imageUrls.length} 张图片`);
    return imageUrls;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片列表
  useEffect(() => {
    const imageUrls = getConnectedImageNodes();
    setConnectedImages((prev) => {
      const merged = mergeImageLists(prev, imageUrls);
      return arraysEqual(prev, merged) ? prev : merged;
    });
  }, [getConnectedImageNodes]);

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 2. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        toast.error('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n');
      console.log('使用提示词:', prompt);
      
      // 3. 获取图片限制
      const imageLimit = getImageLimit();
      
      // 验证图片数量
      if (connectedImages.length > imageLimit) {
        if (channel === 'hailuo-02') {
          toast.warning('海螺02最多支持2张图片（首帧+尾帧），将使用前2张');
        } else {
          toast.warning('海螺2.3只支持单张图片，将使用第一张');
        }
      }
      
      console.log('使用参考图片:', connectedImages.slice(0, imageLimit));
      
      // 4. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 5. 根据通道和是否有参考图动态选择 app_name
      const hasReferenceImage = connectedImages.length > 0;
      let appName;
      if (channel === 'hailuo-02') {
        appName = hasReferenceImage 
          ? 'fal-ai/minimax/hailuo-02/standard/image-to-video'
          : 'fal-ai/minimax/hailuo-02/standard/text-to-video';
      } else {
        // 海螺2.3
        appName = hasReferenceImage
          ? 'fal-ai/minimax/hailuo-2.3/standard/image-to-video'
          : 'fal-ai/minimax/hailuo-2.3/standard/text-to-video';
      }
      
      // 6. 构建请求参数
      const inputParams = {
        prompt: prompt,
        duration: duration,
        resolution: DEFAULT_RESOLUTION,
        prompt_optimizer: promptOptimizer
      };
      
      // 根据通道和图片数量添加图片参数
      if (connectedImages.length > 0) {
        inputParams.image_url = connectedImages[0];
        
        // 海螺02支持尾帧
        if (channel === 'hailuo-02' && connectedImages.length > 1) {
          inputParams.end_image_url = connectedImages[1];
        }
      }
      
      console.log('请求参数:', {
        app_name: appName,
        input_params: inputParams
      });
      
      // 7. 调用 API 创建任务
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: appName,
          input_params: inputParams
        }
      );
      
      console.log('任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        
        // 根据模式提示不同的信息
        let taskTypeLabel = '文生视频';
        if (connectedImages.length > 0) {
          // 海螺02支持首尾帧
          if (channel === 'hailuo-02' && connectedImages.length > 1) {
            taskTypeLabel = '首尾帧视频';
          } else {
            taskTypeLabel = '图生视频';
          }
        }
        
        toast.success(`${taskTypeLabel}任务已创建！`);
        
        // 8. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450,
          y: currentNode.position.y,
        };
        
        // 9. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: taskTypeLabel,
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 10. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建视频任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('视频生成失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 获取图片限制提示
  const getImageLimitHint = () => {
    if (channel === 'hailuo-02') {
      return '最多2张（首帧+尾帧）';
    } else {
      return '最多1张（首帧）';
    }
  };

  // 获取图片限制数量
  const getImageLimit = () => {
    if (channel === 'hailuo-02') {
      return 2;
    }
    return 1; // 海螺2.3只支持1张
  };

  // 处理拖动开始
  const handleDragStart = (e, index) => {
    if (readOnly) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  // 处理拖动结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 处理拖动经过
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // 处理放置
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex || readOnly) return;

    const newImages = [...connectedImages];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setConnectedImages(newImages);
    setDraggedIndex(null);
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    console.log('Hailuo23VideoNode - 验证连接:', connection);
    
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      console.log('Hailuo23VideoNode - 文本连接验证, 源节点:', sourceNode?.type);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      console.log('Hailuo23VideoNode - 图片连接验证, 源节点类型:', sourceNode?.type);
      
      const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'videoTask'];
      
      if (!sourceNode || !validImageTypes.includes(sourceNode.type)) {
        console.log('Hailuo23VideoNode - 节点类型验证失败, 源节点:', sourceNode);
        return false;
      }
      
      // 检查当前已连接的图片数量
      const currentEdges = getEdges();
      const imageEdgesCount = currentEdges.filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      
      console.log('Hailuo23VideoNode - 当前已连接图片数量:', imageEdgesCount);
      
      const limit = getImageLimit();
      if (imageEdgesCount >= limit) {
        const limitHint = channel === 'hailuo-02' ? '2张图片（首帧+尾帧）' : '单张图片';
        toast.warning(`${channel === 'hailuo-02' ? '海螺02' : '海螺2.3'}最多支持${limitHint}`);
        return false;
      }
      
      console.log('Hailuo23VideoNode - 图片连接验证通过');
      return true;
    }
    
    return true;
  }, [channel, getNode, getEdges, id, getImageLimit]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'hailuo23Video', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 处理通道切换
  const handleChannelChange = (newChannel) => {
    setChannel(newChannel);
  };

  // 处理时长变化
  const handleDurationChange = (newDuration) => {
    setDuration(newDuration);
  };

  return (
    <div className="Hailuo23VideoNode-container">
      {/* 复制按钮 */}
      <button 
        className="Hailuo23VideoNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="Hailuo23VideoNode-handle Hailuo23VideoNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '25%' }}
      >
        <div 
          className="Hailuo23VideoNode-handle-label Hailuo23VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="Hailuo23VideoNode-handle Hailuo23VideoNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '75%' }}
      >
        <div 
          className="Hailuo23VideoNode-handle-label Hailuo23VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多1张）"
        >
          图片
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="Hailuo23VideoNode-handle Hailuo23VideoNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="Hailuo23VideoNode-title">海螺视频</div>

      {/* 内容区域 */}
      <div className="Hailuo23VideoNode-wrapper">
        {/* 通道选择 */}
        <div className="Hailuo23VideoNode-field">
          <label className="Hailuo23VideoNode-label">通道</label>
          <div className="Hailuo23VideoNode-button-group">
            {channelOptions.map((option) => (
              <button
                key={option.value}
                className={`Hailuo23VideoNode-button ${channel === option.value ? 'Hailuo23VideoNode-button-active' : ''}`}
                onClick={() => handleChannelChange(option.value)}
                disabled={isGenerating || readOnly}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 时长选择 */}
        <div className="Hailuo23VideoNode-field">
          <label className="Hailuo23VideoNode-label">时长</label>
          <div className="Hailuo23VideoNode-button-group">
            {durationOptions.map((option) => (
              <button
                key={option.value}
                className={`Hailuo23VideoNode-button ${duration === option.value ? 'Hailuo23VideoNode-button-active' : ''}`}
                onClick={() => handleDurationChange(option.value)}
                disabled={isGenerating || readOnly}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 提示词优化器 */}
        <div className="Hailuo23VideoNode-field">
          <label className="Hailuo23VideoNode-label">
            <input
              type="checkbox"
              checked={promptOptimizer}
              onChange={(e) => setPromptOptimizer(e.target.checked)}
              disabled={isGenerating || readOnly}
              className="Hailuo23VideoNode-checkbox"
            />
            <span className="Hailuo23VideoNode-checkbox-label">启用提示词优化器</span>
          </label>
          <div className="Hailuo23VideoNode-hint-small">
            💡 自动优化提示词以获得更好效果
          </div>
        </div>

        {/* 参考图片展示 */}
        <div className="Hailuo23VideoNode-field">
          <label className="Hailuo23VideoNode-label">
            参考图片（{getImageLimitHint()}）
          </label>
          {connectedImages.length > 0 ? (
            // 海螺02支持首尾帧双图展示
            channel === 'hailuo-02' && connectedImages.length > 1 ? (
              // 海螺02 多图展示（支持拖拽排序）
              <div className="Hailuo23VideoNode-image-grid nodrag">
                {connectedImages.slice(0, 2).map((imageUrl, index) => (
                  <div
                    key={`${imageUrl}-${index}`}
                    className={`Hailuo23VideoNode-image-thumb nodrag ${
                      draggedIndex === index ? 'Hailuo23VideoNode-image-dragging' : ''
                    }`}
                    draggable={!readOnly}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <img 
                      src={imageUrl} 
                      alt={`参考图 ${index + 1}`}
                      className="Hailuo23VideoNode-thumb-img"
                    />
                    <div className="Hailuo23VideoNode-thumb-index">
                      {index + 1}
                    </div>
                    <div className="Hailuo23VideoNode-thumb-role">
                      {index === 0 ? '首帧' : '尾帧'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // 单图展示
              <div className="Hailuo23VideoNode-image-preview nodrag">
                <img 
                  src={connectedImages[0]} 
                  alt="首帧参考图"
                  className="Hailuo23VideoNode-preview-img"
                />
                {connectedImages.length > 1 && channel !== 'hailuo-02' && (
                  <div className="Hailuo23VideoNode-image-warning">
                    ⚠️ 只会使用第一张图片
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="Hailuo23VideoNode-image-placeholder">
              🖼️ 连接图片节点添加参考图片（可选）
            </div>
          )}
        </div>

        {/* 连接提示 */}
        <div className="Hailuo23VideoNode-hint">
          💡 <strong>使用说明</strong><br />
          {channel === 'hailuo-02' ? (
            <>
              • <strong>海螺02</strong>：新一代视频生成模型<br />
              • <strong>支持首尾帧</strong>：精确控制视频起止画面<br />
              • <strong>物理模拟</strong>：真实世界物理交互<br />
            </>
          ) : (
            <>
              • <strong>文生视频</strong>：仅通过文字描述生成视频<br />
              • <strong>图生视频</strong>：基于参考图片生成动画<br />
              • <strong>真实人体运动</strong>：专门优化用于捕捉自然动作<br />
              • <strong>电影级特效</strong>：支持复杂的动态场景<br />
            </>
          )}
          点击生成后会自动创建视频任务节点
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`Hailuo23VideoNode-generate ${isGenerating || readOnly ? 'Hailuo23VideoNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="Hailuo23VideoNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

Hailuo23VideoNode.displayName = 'Hailuo23VideoNode';

